import { ViewContainerRef } from "@angular/core";
import { IContentControlManager } from 'app/js/src/domain/content-control/manager';
import { IEvrideoDialogService } from "app/js/src/domain/common/i-evrideo-dialog-service";
import { IContentControlStudioTimeline } from "../content-control-studio-timeline/i-content-control-studio-timeline";
import {
  ContentControlledEditingDirective,
  ContentControlledEditingDirectiveScreenshot,
  ContentControlledEditingDirectiveViewModel,
} from 'app/js/src/domain/content-control/editing-directive';
import { ContentControlledKeyframe } from 'app/js/src/domain/content-control/keyframe';
import {
  ContentControlledEventActionEnum,
  ContentControlledMediaTypeEnum,
} from 'app/js/src/domain/content-control/enums';
import { ContentControlCanvasService } from 'app/js/src/infrastructure/content-control/content-control-canvas.service';
import {
  CCBlurRect,
  CCMosaicRect,
  CCRect,
  ICanvasBaseObject,
} from 'app/js/src/domain/content-control/canvas';
import { ContentControlStudioSubtitleEditorComponent } from "../content-control-studio-subtitle-editor/content-control-studio-subtitle-editor.component";
import { IStudioViewModelContext } from "./i-studio-view-model-context";
import { StudioSegmentController } from "./studio-segment-controller";
import { EffectAreaInteractionHandler } from "./effect-area-interaction-handler";
import { StudioAudioManager } from "./studio-audio-manager";
import { StudioTimelineZoomManager } from "./studio-timeline-zoom-manager";

export class StudioEventCoordinator {
  constructor(
    private readonly manager: IContentControlManager,
    private readonly canvasService: ContentControlCanvasService,
    private readonly _timeline: IContentControlStudioTimeline,
    private readonly dialogService: IEvrideoDialogService,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly _context: IStudioViewModelContext,
    private readonly _segments: StudioSegmentController,
    private readonly _effectArea: EffectAreaInteractionHandler,
    private readonly _audio: StudioAudioManager,
    private readonly _zoom: StudioTimelineZoomManager,
  ) {}

  createEventFromSegment(
    segment: ContentControlledEditingDirectiveViewModel,
    action?: ContentControlledEventActionEnum,
    keyframe?: ContentControlledKeyframe,
  ): ContentControlledEditingDirective {
    const resolvedAction = action ?? segment.action;
    const keyframes =
      keyframe != null
        ? [keyframe]
        : (segment.keyframes?.length
            ? segment.keyframes.map((k) => k.clone())
            : []);
    return new ContentControlledEditingDirective(
      segment.id,
      segment.mediaType,
      segment.startsAtMs,
      segment.endsAtMs,
      segment.title,
      segment.isApproved,
      segment.isMitigated,
      resolvedAction,
      keyframes,
      segment.startFrame,
      segment.endFrame,
      segment.instructions,
      segment.subtitles,
      segment.createdAt,
      segment.mediaId,
    );
  }

  async segmentToEvent(
    segment: ContentControlledEditingDirectiveViewModel,
    action?: ContentControlledEventActionEnum,
    keyframe?: ContentControlledKeyframe,
    doPostActions: boolean = true,
  ): Promise<boolean> {
    if (!segment) return false;

    let event = this.manager.editingDirectives.find((x) => x.id == segment.id);
    let sameAction = true;
    const overlappingEvents = this.getSegmentOverlappingEvents(segment);

    if (!event) {
      segment.isMitigated = !segment.isApproved;
      event = this.createEventFromSegment(segment, action, keyframe);
      if (
        event.action === ContentControlledEventActionEnum.Cut &&
        overlappingEvents &&
        overlappingEvents.length > 0
      ) {
        if (await this.openCutEventDialog(overlappingEvents)) {
          this.manager.upsertCutEditingDirectiveEvent(event, overlappingEvents, this._audio?.subtitleOptions || []);
        } else {
          return false;
        }
      } else {
        this.manager.addEditingDirectiveEvent(event);
      }
      this.manager.updateSelectedSegment(segment);
      if (this.manager.selectedSegment.isConfigurationComplete && doPostActions) {
        this._segments.changeSelectedSegment(-1);
      }
    } else {
      sameAction = segment.action === event.action || !doPostActions;
      if (
        doPostActions &&
        this._segments.multiEventsSelectedIds.length > 0 &&
        this._segments.multiEventsSelectedIds.includes(event.id) &&
        event.action !== ContentControlledEventActionEnum.Cut
      ) {
        this.applyToChildEvents(
          event.id,
          (x: ContentControlledEditingDirectiveViewModel) => {
            x.instructions = segment.instructions ? { ...segment.instructions } : x.instructions;
            this.manager.updateEditingDirectiveEventFromSegment(x, null);
          },
        );
      }
      if (
        event.action === ContentControlledEventActionEnum.EditSubtitles &&
        (event.startsAtMs != segment.startsAtMs || event.endsAtMs != segment.endsAtMs)
      ) {
        (this._audio?.subtitleOptions ?? []).map((x) =>
          segment.subtitles.mergeSubtitleSegments(
            x,
            this.manager.getSubtitleSegments(x, segment.startsAtMs, segment.endsAtMs),
          ),
        );
      }
      if (
        event.action === ContentControlledEventActionEnum.Cut &&
        overlappingEvents &&
        overlappingEvents.length > 0
      ) {
        if (await this.openCutEventDialog(overlappingEvents)) {
          this.manager.upsertCutEditingDirectiveEvent(
            event, overlappingEvents, this._audio?.subtitleOptions || [], segment,
          );
        } else {
          return false;
        }
      } else {
        this.manager.updateEditingDirectiveEventFromSegment(segment, keyframe);
      }
      if (this.manager.selectedSegment && event.id === this.manager.selectedSegment.id) {
        this.manager.updateSelectedSegment(segment);
      }
    }

    if (event.mediaType == ContentControlledMediaTypeEnum.Video) {
      const canvasObject = this._effectArea.getCanvasObjectFromEvent(event);
      switch (event.action) {
        case ContentControlledEventActionEnum.BlackRect:
        case ContentControlledEventActionEnum.BlackScreen:
          !sameAction && this.canvasService.removeBlackRectangle(event.id);
          doPostActions && this.canvasService.updateBlackRectangle(canvasObject as CCRect);
          break;
        case ContentControlledEventActionEnum.PixelateRect:
        case ContentControlledEventActionEnum.PixelateScreen:
          !sameAction && this.canvasService.removeMosaicRectangle(event.id);
          doPostActions && this.canvasService.updateMosaicRectangle(canvasObject as CCMosaicRect);
          break;
        case ContentControlledEventActionEnum.PixelateOval:
          !sameAction && this.canvasService.removeMosaicCircle(event.id);
          doPostActions && this.canvasService.updateMosaicCircle(canvasObject as CCMosaicRect);
          break;
        case ContentControlledEventActionEnum.BlurRect:
        case ContentControlledEventActionEnum.BlurScreen:
          !sameAction && this.canvasService.removeBlurRectangle(event.id);
          doPostActions && this.canvasService.updateBlurRectangle(canvasObject as CCBlurRect);
          break;
        case ContentControlledEventActionEnum.BlurOval:
          !sameAction && this.canvasService.removeBlurCircle(event.id);
          doPostActions && this.canvasService.updateBlurCircle(canvasObject as CCBlurRect);
          break;
        case ContentControlledEventActionEnum.BlackOval:
          !sameAction && this.canvasService.removeBlackCircle(event.id);
          doPostActions && this.canvasService.updateBlackCircle(canvasObject as CCRect);
          break;
      }
    }
    doPostActions && !this._zoom.isFitSegment && this._timeline.rebuildSegmentsInternally();
    return true;
  }

  openSubtitleEditorDialog(segment: ContentControlledEditingDirectiveViewModel): void {
    this._context.isFragmentDialogOpen = true;
    const sub = segment.subtitles.clone();
    this.dialogService
      .openDialog(this.viewContainerRef, {
        title: "Subtitles",
        type: "info",
        cancelButtonText: "cancel",
        confirmButtonText: "apply",
        hideIcon: true,
        content: ContentControlStudioSubtitleEditorComponent,
        contentContext: {
          container: sub,
          timeFormatFunction: this._context.timeFormatFunction.bind(this._context),
          mediaStartTimeStampMs: this.manager.mediaStartTimeStampMs,
        },
      })
      .then(async (result) => {
        if (result) {
          segment.subtitles = sub;
          await this.segmentToEvent(segment);
        }
      })
      .finally(() => this._context.isFragmentDialogOpen = false);
  }

  async openCutEventDialog(overlappingEvents: ContentControlledEditingDirective[]): Promise<boolean> {
    return await this.dialogService.openDialog(this.viewContainerRef, {
      type: "warning",
      title: "Change effecting existing events",
      content: `Applying this cut will effect ${overlappingEvents.length} other events, do you wish to continue?`,
      cancelButtonText: "Cancel",
      confirmButtonText: "Yes, Continue",
    });
  }

  getSegmentOverlappingEvents(
    segment: ContentControlledEditingDirectiveViewModel,
  ): ContentControlledEditingDirective[] {
    return this.manager.editingDirectives.filter((x) => {
      if (x.id === segment.id) return false;
      const one = x.startsAtMs <= segment.startsAtMs;
      const two = x.endsAtMs >= segment.startsAtMs;
      const three = segment.startsAtMs <= x.startsAtMs;
      const four = segment.endsAtMs >= x.startsAtMs;
      return (one && two) || (three && four);
    });
  }

  afterScreenshotTaken(result: ContentControlledEditingDirectiveScreenshot): void {
    if (result && result.id && result.screenshot) {
      const segment = this._segments.segments.find((x) => x.id === result.id);
      if (segment) {
        if (result.type === "in") {
          segment.startFrame = result.screenshot;
        } else {
          segment.endFrame = result.screenshot;
        }
        const segmentIsEvent = this.manager.editingDirectives.find((x) => x.id === segment.id);
        if (segmentIsEvent) {
          if (result.type === "in") {
            segmentIsEvent.startFrame = segment.startFrame;
          } else {
            segmentIsEvent.endFrame = segment.endFrame;
          }
        }
      }
    }
  }

  private applyToChildEvents(id: number, actionFunc: any): void {
    const childEvents = this._segments.multiEventsSelectedIds.filter((x) => x != id);
    if (actionFunc && childEvents && childEvents.length > 0) {
      childEvents.forEach(async (childId) => {
        await actionFunc(this._segments.segments.find((x) => x.id == childId));
      });
    }
  }
}
